import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FormEditor from "@/components/FormEditor";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Download, Lock, PenLine, Unlock, Crown, Layers, Building2, ExternalLink, Plus } from "lucide-react";
import { Helmet } from "react-helmet-async";
import formContent from "@/lib/formContent.json";
import { humanizeLabel } from "@/lib/humanize";
import { addRecentForm } from "@/lib/recentForms";
import { TOP_COUNTIES } from "@/lib/ncCounties";
import { GuestCheckoutDialog } from "@/components/GuestCheckoutDialog";
import { useI18n } from "@/context/I18nContext";
import { SaveBadge } from "@/components/SaveBadge";

export default function FormDetail() {
  const { number } = useParams();
  const [params, setParams] = useSearchParams();
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [editing, setEditing] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [access, setAccess] = useState(false);
  const [buying, setBuying] = useState(false);
  const [related, setRelated] = useState([]);
  const [bundling, setBundling] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);
  const navigate = useNavigate();

  const subId = params.get("submission");
  const subscribed = user && (["active", "trialing"].includes(user.subscription_status) || user.role === "admin");
  const isSos = form?.source === "sos";
  const sosFlat = isSos && form?.has_fillable_pdf === false;
  const sosPortal = form?.detail_url || "https://www.sosnc.gov/online_services/";

  const addAllToMyForms = async () => {
    if (!user) { navigate("/login"); return; }
    if (!subscribed) { toast.info(t("detail.goProToast")); navigate("/pricing"); return; }
    setBundling(true);
    const nums = [form.number, ...related.map((r) => r.number)];
    let ok = 0;
    for (const num of nums) {
      try {
        await api.post("/submissions", { form_number: num, field_values: {} });
        ok++;
      } catch { /* skip inaccessible */ }
    }
    setBundling(false);
    toast.success(t("detail.addedForms", { count: ok }));
    navigate("/saved");
  };

  useEffect(() => {
    api.get(`/forms/${number}`).then(({ data }) => { setForm(data); addRecentForm({ number: data.number, title: data.title }); }).catch(() => setNotFound(true));
    setRelated([]);
    api.get(`/forms/${number}/related`).then(({ data }) => setRelated(data.related || [])).catch(() => {});
  }, [number]);

  useEffect(() => {
    if (!user) { setAccess(false); return; }
    if (subscribed) { setAccess(true); return; }
    api.get(`/forms/${number}/access`).then(({ data }) => setAccess(!!data.access)).catch(() => setAccess(false));
  }, [user, subscribed, number]);

  useEffect(() => {
    if (subId && access) {
      api.get(`/submissions/${subId}`).then(({ data }) => { setSubmission(data); setEditing(true); }).catch(() => {});
    }
  }, [subId, access]);

  useEffect(() => {
    if (params.get("edit") === "1" && access) setEditing(true);
  }, [access, params]);

  // Carry over a locally-saved draft (e.g. entries typed before a guest paid) when
  // opening the editor without an existing server submission.
  const editorInitialValues = useMemo(() => {
    if (submission?.field_values && Object.keys(submission.field_values).length) return submission.field_values;
    try { return JSON.parse(localStorage.getItem(`nc_draft_${number}`) || "{}"); } catch { return {}; }
  }, [submission, number, editing]);

  const resumedDraft = useMemo(() => {
    if (submission?.field_values && Object.keys(submission.field_values).length) return false;
    try { return Object.keys(JSON.parse(localStorage.getItem(`nc_draft_${number}`) || "{}")).length > 0; } catch { return false; }
  }, [submission, number, editing]);

  const buyForm = async () => {
    if (!user) { setGuestOpen(true); return; }
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout-form", { form_number: number, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("detail.checkoutFail"));
      setBuying(false);
    }
  };

  const guestBuyForm = async (email) => {
    try {
      const { data } = await api.post("/payments/guest/checkout-form", {
        email, accepted_terms: true, form_number: number, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("detail.checkoutFail"));
    }
  };

  if (notFound) return (<div><Navbar /><div className="max-w-3xl mx-auto px-6 py-32 text-center"><h1 className="font-serif text-4xl mb-4">{t("detail.notFound")}</h1><Link to="/forms"><Button variant="outline" className="rounded-sm">{t("detail.backLibrary")}</Button></Link></div><Footer /></div>);
  if (!form) return <div className="min-h-screen grid place-items-center text-muted-foreground">{t("detail.loading")}</div>;

  return (
    <div>
      <Navbar />
      <Helmet>
        <title>{`${form.number} — ${form.title} | NC Court Form | NC Legal Forms`}</title>
        <meta name="description" content={(formContent[form.number]?.meta) || `Fill, print and download the official North Carolina ${form.number} form — ${form.title}. ${(form.description || "").slice(0, 150)} Complete online with AI assistance for any NC county.`} />
        <link rel="canonical" href={`${process.env.REACT_APP_BACKEND_URL || ""}/forms/${form.number}`} />
        <meta property="og:title" content={`${form.number} — ${form.title}`} />
        <meta property="og:description" content={(formContent[form.number]?.meta) || (form.description || `Official NC ${form.category} court form.`).slice(0, 200)} />
      </Helmet>
      <section className="max-w-7xl mx-auto px-6 pt-10 pb-6 no-print">
        <Link to="/forms" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft size={16} /> {t("detail.formsLibrary")}</Link>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <Badge variant="outline" className="rounded-sm font-mono">{form.number}</Badge>
              <Badge className="rounded-sm bg-primary">{form.category}</Badge>
              {isSos && <Badge className="rounded-sm bg-emerald-600 hover:bg-emerald-600">Free · Fillable</Badge>}
              {sosFlat && (
                <a href={sosPortal} target="_blank" rel="noreferrer" data-testid="sos-file-online-badge"
                  className="inline-flex items-center gap-1.5 rounded-sm bg-accent text-accent-foreground px-2.5 py-1 text-xs font-medium hover:bg-primary hover:text-primary-foreground transition-colors">
                  <Building2 size={13} /> File online at NC SOS <ExternalLink size={12} />
                </a>
              )}
            </div>
            <h1 className="font-serif font-bold text-4xl sm:text-5xl tracking-tight leading-[1.05] mb-4">{form.title}</h1>
            <p className="text-muted-foreground leading-relaxed">{form.description}</p>
            {form.statute && <p className="text-sm text-muted-foreground mt-3"><span className="font-medium text-foreground">{t("detail.statute")}</span> {form.statute}</p>}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        {editing ? (
          <FormEditor form={form} submissionId={access ? submission?.id : undefined} initialValues={editorInitialValues} resumedDraft={resumedDraft} onSaved={(id) => { if (id) { setSubmission((s) => ({ ...(s || {}), id })); setParams({ submission: id }); } }} preview={!access} />
        ) : (
          <div className="grid lg:grid-cols-3 gap-8 no-print">
            <div className="lg:col-span-2 space-y-8">
              {formContent[form.number] && (
                <div className="grid-card p-8" data-testid="form-about">
                  <p className="overline text-accent mb-3">About this form</p>
                  <p className="text-muted-foreground leading-relaxed">{formContent[form.number].blurb}</p>
                  {formContent[form.number].usedFor?.length > 0 && (
                    <div className="mt-5">
                      <p className="text-sm font-medium mb-2">Commonly used for</p>
                      <div className="flex flex-wrap gap-2">
                        {formContent[form.number].usedFor.map((u, i) => (
                          <span key={i} className="text-xs px-3 py-1.5 border-2 border-black bg-secondary">{u}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="grid-card p-8">
                <p className="overline text-accent mb-4">{t("detail.whatsOn", { count: form.fields?.length || 0 })}</p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                  {(form.fields || []).slice(0, 40).map((f) => (
                    <div key={f.name} className="flex items-center gap-2 text-sm py-1.5 border-b border-border/60">
                      <span className="w-1.5 h-1.5 bg-accent rounded-full shrink-0" /><span className="truncate">{humanizeLabel(f.label)}</span>
                    </div>
                  ))}
                </div>
                {(form.fields?.length || 0) > 40 && <p className="text-sm text-muted-foreground mt-4">{t("detail.moreFields", { count: form.fields.length - 40 })}</p>}
              </div>
            </div>

            <div className="grid-card p-8 h-fit lg:sticky lg:top-24">
              {!user ? (
                <>
                  <Lock className="text-accent mb-4" />
                  <h3 className="font-serif text-2xl mb-2">{t("detail.loginTitle")}</h3>
                  <p className="text-sm text-muted-foreground mb-5">{t("detail.loginDesc")}</p>
                  <Button onClick={() => setGuestOpen(true)} data-testid="guest-buy-btn" className="w-full rounded-sm gap-2 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground transition-colors">
                    <Unlock size={16} /> Buy this form — $3.95 (no account)
                  </Button>
                  <Button onClick={() => setEditing(true)} data-testid="try-preview-btn" variant="outline" className="w-full rounded-sm gap-2 mt-3 hover:bg-secondary">
                    <PenLine size={16} /> {t("detail.tryPreview")}
                  </Button>
                  <div className="flex items-center gap-3 my-4"><span className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">{t("detail.or")}</span><span className="h-px flex-1 bg-border" /></div>
                  <Link to="/login"><Button variant="outline" data-testid="detail-login-btn" className="w-full rounded-sm hover:bg-secondary">{t("detail.loginBtn")}</Button></Link>
                  {form.pdf_url && <a href={form.pdf_url} target="_blank" rel="noreferrer" className="block mt-3">
                    <Button variant="ghost" className="w-full rounded-sm gap-2"><Download size={16} /> {t("detail.downloadBlankFree")}</Button>
                  </a>}
                </>
              ) : access ? (
                <>
                  <PenLine className="text-accent mb-4" />
                  <h3 className="font-serif text-2xl mb-2">{t("detail.readyTitle")}</h3>
                  <p className="text-sm text-muted-foreground mb-6">{t("detail.readyDesc")}</p>
                  {sosFlat ? (
                    <a href={sosPortal} target="_blank" rel="noreferrer" data-testid="sos-file-online-btn">
                      <Button className="w-full rounded-sm gap-2 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground transition-colors"><Building2 size={16} /> File online at NC SOS <ExternalLink size={14} /></Button>
                    </a>
                  ) : (
                    <Button onClick={() => setEditing(true)} data-testid="fill-online-btn" className="w-full rounded-sm bg-primary hover:bg-accent hover:text-accent-foreground transition-colors gap-2"><PenLine size={16} /> {t("detail.fillOnline")}</Button>
                  )}
                  {form.pdf_url && <a href={form.pdf_url} target="_blank" rel="noreferrer" className="block mt-3">
                    <Button variant="outline" className="w-full rounded-sm gap-2 hover:bg-secondary"><Download size={16} /> {t("detail.downloadBlank")}</Button>
                  </a>}
                </>
              ) : (
                <>
                  <Lock className="text-accent mb-4" />
                  <h3 className="font-serif text-2xl mb-2">{t("detail.unlockTitle")}</h3>
                  <p className="text-sm text-muted-foreground mb-5">{t("detail.unlockDesc")}</p>
                  <Button onClick={buyForm} disabled={buying} data-testid="unlock-form-btn" className="w-full rounded-sm gap-2 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground transition-colors">
                    <Unlock size={16} /> {buying ? t("detail.redirecting") : (!user ? "Buy this form — $3.95 (no account)" : t("detail.unlockBtn"))}
                  </Button>
                  <Button onClick={() => setEditing(true)} data-testid="try-preview-btn" variant="outline" className="w-full rounded-sm gap-2 mt-3 hover:bg-secondary">
                    <PenLine size={16} /> {t("detail.tryPreview")}
                  </Button>
                  <div className="flex items-center gap-3 my-4"><span className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">{t("detail.or")}</span><span className="h-px flex-1 bg-border" /></div>
                  <Link to="/pricing"><Button data-testid="detail-upgrade-btn" variant="outline" className="w-full rounded-sm gap-2 hover:bg-secondary"><Crown size={16} /> {t("detail.goPro")}</Button></Link>
                  {form.pdf_url && <a href={form.pdf_url} target="_blank" rel="noreferrer" className="block mt-3">
                    <Button variant="ghost" className="w-full rounded-sm gap-2"><Download size={16} /> {t("detail.downloadBlankFree")}</Button>
                  </a>}
                </>
              )}
            </div>
          </div>
        )}

        {related.length > 0 && (
          <div className="mt-16 no-print" data-testid="related-forms-section">
            <p className="overline text-accent mb-2">{t("detail.fileConjunction")}</p>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
              <h2 className="font-serif font-bold text-3xl tracking-tight">{t("detail.oftenFiled", { number: form.number })}</h2>
              <div className="flex flex-wrap gap-2">
                <Link to={`/bundle?${[form.number, ...related.map((r) => r.number)].slice(0, 3).map((n) => `add=${encodeURIComponent(n)}`).join("&")}`} data-testid="bundle-companions-btn">
                  <Button variant="outline" className="rounded-sm gap-2 hover:bg-secondary">
                    <Layers size={16} /> Bundle these — $9.95 <SaveBadge testid="bundle-companions-save-badge" />
                  </Button>
                </Link>
                <Button onClick={addAllToMyForms} disabled={bundling} data-testid="add-all-forms-btn"
                  className="rounded-sm gap-2 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
                  <Layers size={16} /> {bundling ? t("detail.adding") : t("detail.addAll")}
                </Button>
              </div>
            </div>
            <p className="text-muted-foreground mb-6 max-w-2xl">{t("detail.relatedDesc")}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((r) => (
                <div key={r.number} data-testid={`related-form-${r.number}`}
                  className="grid-card p-5 group hover:-translate-y-1 transition-transform flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="outline" className="rounded-sm font-mono text-xs">{r.number}</Badge>
                    <Layers size={16} className="text-accent" />
                  </div>
                  <Link to={`/forms/${r.number}`} className="flex-1">
                    <h3 className="font-serif text-lg leading-tight mb-2 group-hover:text-accent transition-colors">{r.title}</h3>
                    {r.reason && <p className="text-sm text-muted-foreground leading-relaxed">{r.reason}</p>}
                  </Link>
                  <Link to={`/bundle?add=${encodeURIComponent(form.number)}&add=${encodeURIComponent(r.number)}`}
                    data-testid={`related-add-bundle-${r.number}`}
                    className="mt-4 inline-flex items-center justify-center gap-1.5 h-9 border-2 border-black/15 hover:border-accent hover:text-accent text-sm font-medium transition-colors">
                    <Plus size={15} /> Add to bundle
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {!isSos && (
        <div className="mt-16 no-print" data-testid="form-counties-section">
          <p className="overline text-accent mb-2">File in your county</p>
          <h2 className="font-serif font-bold text-3xl tracking-tight mb-2">File {form.number} in your North Carolina county</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl">This is a statewide AOC form accepted in all 100 NC counties. Get county-specific filing info and courthouse details:</p>
          <div className="flex flex-wrap gap-2.5">
            {TOP_COUNTIES.map((c) => (
              <Link key={c.slug} to={`/nc/${c.slug}`} data-testid={`form-county-${c.slug}`}
                className="px-4 py-2 border-2 border-black hover:bg-accent hover:text-accent-foreground transition-colors text-sm font-medium">
                {c.name} County
              </Link>
            ))}
            <Link to="/nc" className="px-4 py-2 border-2 border-black bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground transition-colors text-sm font-medium inline-flex items-center gap-1" data-testid="form-all-counties">
              All 100 counties <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        )}
      </section>
      <GuestCheckoutDialog open={guestOpen} onOpenChange={setGuestOpen}
        title="Buy this form" priceLabel="$3.95" upsellHref={`/bundle?add=${number}`} onSubmit={guestBuyForm} />
      <Footer />
    </div>
  );
}
